import type { LongTermPricing, ShortTermPricing } from '@carvia/ros-client-types';
import { type BookedExtra, type CustomBookedExtraDto } from '@carvia/ros-client-types';
import { subject } from '@casl/ability';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import type { Car } from '@/entities/cars/car.entity';
import { DateTime } from 'luxon';
import { calculateRentalDays } from './calculate-rental-days';
import { roundDecimals } from './round-decimals';
import { getStationOfCarByDate } from '@/hooks/use-current-station-of-car';
import { LONG_TERM_MIN_DAYS } from './long-term-create.utils';
import { periodExtraOptionPrice } from './period-extra-option-price';
import { REGULAR_TAX_PERCENT } from './tax-procedure';

export const DEFAULT_BOOKING_TIMEZONE = 'Europe/Berlin';

type LongTermPricingPeriod = LongTermPricing['periods'][number];

export function bookingStartTimezone(booking: {
  pickupStation?: { timezone?: string } | null;
  startDate?: Date | string;
  allocations?: { car?: Car }[];
  bookedCar?: Car | null;
}): string {
  if (booking.pickupStation?.timezone) {
    return booking.pickupStation.timezone;
  }
  const car = booking.allocations?.[0]?.car ?? booking.bookedCar ?? undefined;
  if (car?.stations?.length && booking.startDate) {
    try {
      return getStationOfCarByDate(car, booking.startDate).timezone || DEFAULT_BOOKING_TIMEZONE;
    } catch {
      return DEFAULT_BOOKING_TIMEZONE;
    }
  }
  return DEFAULT_BOOKING_TIMEZONE;
}

export function getPricingSubject(
  pricing: { type?: string } | null | undefined,
): 'ShortTermPricing' | 'LongTermPricing' {
  return pricing?.type === 'LONG_TERM' ? 'LongTermPricing' : 'ShortTermPricing';
}

export function pricingSubject<T extends { type?: string }>(pricing: T) {
  return subject(getPricingSubject(pricing), pricing);
}

export function isShortTermPricing(
  pricing: Pricing | null | undefined,
): pricing is ShortTermPricing {
  return !!pricing && pricing.type !== 'LONG_TERM';
}

export function isLongTermPricing(pricing: Pricing | null | undefined): pricing is LongTermPricing {
  return !!pricing && pricing.type === 'LONG_TERM';
}

export function asShortTermPricing(pricing: Pricing | null | undefined): ShortTermPricing | null {
  return isShortTermPricing(pricing) ? pricing : null;
}

function calendarDay(date: Date | string, timezone: string): DateTime {
  return DateTime.fromJSDate(new Date(date)).setZone(timezone).startOf('day');
}

export function getOverlappingPricingPeriodIndex(
  pricing: LongTermPricing,
  now = new Date(),
  timezone = DEFAULT_BOOKING_TIMEZONE,
): number | null {
  const periods = pricing.periods ?? [];
  if (periods.length === 0) {
    return null;
  }
  const today = calendarDay(now, timezone);
  const overlapping = periods.findIndex((period) => {
    const start = calendarDay(period.startDate, timezone);
    const end = calendarDay(period.endDate, timezone);
    return today >= start && today <= end;
  });
  return overlapping >= 0 ? overlapping : null;
}

export function getCurrentPricingPeriodIndex(
  pricing: LongTermPricing,
  now = new Date(),
  timezone = DEFAULT_BOOKING_TIMEZONE,
): number | null {
  const periods = pricing.periods ?? [];
  if (periods.length === 0) {
    return null;
  }
  const overlapping = getOverlappingPricingPeriodIndex(pricing, now, timezone);
  if (overlapping != null) {
    return overlapping;
  }
  if (calendarDay(now, timezone) < calendarDay(periods[0].startDate, timezone)) {
    return 0;
  }
  return periods.length - 1;
}

export function isStartedPricingPeriod(
  period: { startDate: Date | string },
  now = new Date(),
  timezone = DEFAULT_BOOKING_TIMEZONE,
): boolean {
  return (
    DateTime.fromJSDate(new Date(period.startDate)).setZone(timezone) <=
    DateTime.fromJSDate(now).setZone(timezone)
  );
}

export function getCurrentPricingPeriod(
  pricing: Pricing | null | undefined,
  now = new Date(),
  timezone = DEFAULT_BOOKING_TIMEZONE,
): LongTermPricingPeriod | null {
  if (!isLongTermPricing(pricing)) {
    return null;
  }
  const index = getCurrentPricingPeriodIndex(pricing, now, timezone);
  return index == null ? null : (pricing.periods[index] ?? null);
}

export function getQuotePeriod(
  pricing: LongTermPricing,
  periodIndex?: number | null,
  timezone = DEFAULT_BOOKING_TIMEZONE,
): LongTermPricingPeriod | null {
  if (periodIndex != null) {
    return pricing.periods[periodIndex] ?? null;
  }
  return getCurrentPricingPeriod(pricing, new Date(), timezone);
}

export function getNextPricingPeriod(
  pricing: Pricing | null | undefined,
  now = new Date(),
  timezone = DEFAULT_BOOKING_TIMEZONE,
): LongTermPricingPeriod | null {
  if (!isLongTermPricing(pricing)) {
    return null;
  }
  const index = getCurrentPricingPeriodIndex(pricing, now, timezone);
  if (index == null) {
    return null;
  }
  return pricing.periods[index + 1] ?? null;
}

export function getPeriodDays(
  period: Pick<LongTermPricingPeriod, 'startDate' | 'endDate'>,
): number {
  return calculateRentalDays(new Date(period.startDate), new Date(period.endDate));
}

export function getPeriodBasePrice(period: LongTermPricingPeriod): number {
  return period.customTotalBasePrice ?? period.totalBasePrice ?? 0;
}

export function getPeriodPrice(period: LongTermPricingPeriod): number {
  if (typeof period.totalPrice === 'number') {
    return period.totalPrice;
  }
  const extras = (period.bookedExtras ?? []).reduce(
    (sum, extra) => sum + (extra.calculatedPrice ?? 0),
    0,
  );
  const custom = (period.customBookedExtras ?? []).reduce(
    (sum, extra) => sum + (extra.price ?? 0),
    0,
  );
  return roundDecimals(getPeriodBasePrice(period) + extras + custom, 2);
}

export type PeriodTotalDiff = {
  index: number;
  oldTotal: number | null;
  newTotal: number | null;
  oldStart: Date | string | null;
  oldEnd: Date | string | null;
  newStart: Date | string | null;
  newEnd: Date | string | null;
  datesChanged: boolean;
};

function sameInstant(a: Date | string | null | undefined, b: Date | string | null | undefined) {
  if (a == null || b == null) {
    return a == null && b == null;
  }
  return new Date(a).getTime() === new Date(b).getTime();
}

export function diffLongTermPeriodTotals(
  oldPricing: LongTermPricing,
  newPricing: LongTermPricing,
): PeriodTotalDiff[] {
  const oldPeriods = oldPricing.periods ?? [];
  const newPeriods = newPricing.periods ?? [];
  const length = Math.max(oldPeriods.length, newPeriods.length);
  const rows: PeriodTotalDiff[] = [];
  for (let index = 0; index < length; index++) {
    const oldPeriod = oldPeriods[index];
    const newPeriod = newPeriods[index];
    const oldTotal = oldPeriod ? getPeriodPrice(oldPeriod) : null;
    const newTotal = newPeriod ? getPeriodPrice(newPeriod) : null;
    const oldStart = oldPeriod?.startDate ?? null;
    const oldEnd = oldPeriod?.endDate ?? null;
    const newStart = newPeriod?.startDate ?? null;
    const newEnd = newPeriod?.endDate ?? null;
    const datesChanged =
      oldPeriod != null &&
      newPeriod != null &&
      (!sameInstant(oldStart, newStart) || !sameInstant(oldEnd, newEnd));
    if (oldTotal === newTotal && !datesChanged) {
      continue;
    }
    rows.push({
      index,
      oldTotal,
      newTotal,
      oldStart,
      oldEnd,
      newStart,
      newEnd,
      datesChanged,
    });
  }
  return rows;
}

export function getPeriodAveragePricePerDay(period: LongTermPricingPeriod): number {
  const days = getPeriodDays(period);
  return days > 0 ? roundDecimals(getPeriodPrice(period) / days, 2) : 0;
}

export function getContractTotal(pricing: Pricing): number {
  if (typeof pricing.totalPrice === 'number') {
    return pricing.totalPrice;
  }
  if (!isLongTermPricing(pricing)) {
    return 0;
  }
  return roundDecimals(
    (pricing.periods ?? []).reduce((sum, period) => sum + getPeriodPrice(period), 0),
    2,
  );
}

export function getAveragePricePerDay(pricing: Pricing): number {
  if (!isLongTermPricing(pricing)) {
    return pricing.averagePricePerDay ?? 0;
  }
  const days = calculateRentalDays(new Date(pricing.startDate), new Date(pricing.endDate));
  return days > 0 ? roundDecimals(getContractTotal(pricing) / days, 2) : 0;
}

export function getAverageMonthlyPrice(pricing: Pricing): number {
  return roundDecimals(getAveragePricePerDay(pricing) * LONG_TERM_MIN_DAYS, 2);
}

export function getAllCustomBookedExtras(pricing: Pricing): CustomBookedExtraDto[] {
  if (isShortTermPricing(pricing)) {
    return pricing.customBookedExtras ?? [];
  }
  return (pricing.periods ?? []).flatMap((period) => period.customBookedExtras ?? []);
}

export function getCurrentPricingExtras(
  pricing: Pricing,
  timezone = DEFAULT_BOOKING_TIMEZONE,
): {
  bookedExtras: BookedExtra[];
  customBookedExtras: CustomBookedExtraDto[];
} {
  if (isShortTermPricing(pricing)) {
    return {
      bookedExtras: pricing.bookedExtras ?? [],
      customBookedExtras: pricing.customBookedExtras ?? [],
    };
  }
  const period = getCurrentPricingPeriod(pricing, new Date(), timezone);
  return {
    bookedExtras: period?.bookedExtras ?? [],
    customBookedExtras: period?.customBookedExtras ?? [],
  };
}

export function getCustomBookedExtras(
  pricing: Pricing,
  timezone = DEFAULT_BOOKING_TIMEZONE,
): CustomBookedExtraDto[] {
  return getCurrentPricingExtras(pricing, timezone).customBookedExtras;
}

export function getBookedExtras(
  pricing: Pricing,
  timezone = DEFAULT_BOOKING_TIMEZONE,
): BookedExtra[] {
  return getCurrentPricingExtras(pricing, timezone).bookedExtras;
}

export function getTotalBasePrice(pricing: Pricing, timezone = DEFAULT_BOOKING_TIMEZONE): number {
  if (isShortTermPricing(pricing)) {
    return pricing.totalBasePrice;
  }
  const period = getCurrentPricingPeriod(pricing, new Date(), timezone);
  return period ? getPeriodBasePrice(period) : 0;
}

export function getCustomTotalBasePrice(
  pricing: Pricing,
  timezone = DEFAULT_BOOKING_TIMEZONE,
): number | null {
  if (isShortTermPricing(pricing)) {
    return pricing.customTotalBasePrice ?? null;
  }
  return getCurrentPricingPeriod(pricing, new Date(), timezone)?.customTotalBasePrice ?? null;
}

type PeriodKmFields = {
  customInclusiveKm?: number | null;
  defaultInclusiveKm?: number | null;
};

export function getPeriodInclusiveKm(period: LongTermPricingPeriod): number | null {
  const slice = period as LongTermPricingPeriod & PeriodKmFields;
  return slice.customInclusiveKm ?? slice.defaultInclusiveKm ?? null;
}

function isCouponAdditionalExtra(extra: BookedExtra | CustomBookedExtraDto): boolean {
  return 'isAdditional' in extra && extra.isAdditional === true;
}

function isTransferExtra(extra: BookedExtra | CustomBookedExtraDto): boolean {
  const type =
    'customVehicleExtraType' in extra
      ? extra.customVehicleExtraType
      : extra.vehicleExtraSnapshot?.type;
  return type === 'TRANSFER_PICKUP' || type === 'TRANSFER_RETURN';
}

export function isChosenBookedExtra(extra?: {
  isDefaultOptionSelected?: boolean;
  calculatedPrice?: number | null;
}): boolean {
  if (!extra) {
    return false;
  }
  if (typeof extra.isDefaultOptionSelected === 'boolean') {
    return extra.isDefaultOptionSelected === false;
  }
  return (extra.calculatedPrice ?? 0) > 0;
}

export type PeriodExtraLine = {
  key: string;
  type: string | null;
  fallbackName: string;
  optionValue: string | null;
  price: number;
};

export function getPeriodExtraLines(period: LongTermPricingPeriod): PeriodExtraLine[] {
  const booked = (period.bookedExtras ?? [])
    .filter((extra) => !isTransferExtra(extra) && isChosenBookedExtra(extra))
    .map((extra, index) => ({
      key: `booked:${extra.vehicleExtraSnapshot?.type ?? extra.vehicleExtraSnapshot?.id ?? 'extra'}:${index}`,
      type: extra.vehicleExtraSnapshot?.type ?? null,
      fallbackName:
        extra.vehicleExtraSnapshot?.description ?? extra.vehicleExtraSnapshot?.type ?? '',
      optionValue: extra.vehicleExtraOptionSnapshot?.value ?? null,
      price: extra.calculatedPrice ?? 0,
    }));
  const custom = (period.customBookedExtras ?? [])
    .filter((extra) => !isCouponAdditionalExtra(extra))
    .map((extra, index) => ({
      key: `custom:${extra.id ?? extra.description ?? 'extra'}:${index}`,
      type: extra.customVehicleExtraType ?? null,
      fallbackName: extra.description || extra.customVehicleExtraType || '',
      optionValue: extra.value != null ? String(extra.value) : null,
      price: extra.price ?? 0,
    }));
  return [...booked, ...custom];
}

export type PricingPeriodState = 'invoiced' | 'started' | 'due' | 'unposted';

export function getPricingPeriodState(
  period: LongTermPricingPeriod,
  invoiced: boolean,
  now = new Date(),
  timezone = DEFAULT_BOOKING_TIMEZONE,
): PricingPeriodState {
  if (invoiced) {
    return 'invoiced';
  }
  if (new Date(period.startDate).getTime() <= now.getTime()) {
    return 'started';
  }
  if (calendarDay(now, timezone) >= calendarDay(period.startDate, timezone).minus({ days: 7 })) {
    return 'due';
  }
  return 'unposted';
}

const RENTAL_FEE_PERIOD_LANE = /^rental_fee:(\d+)$/;

export function getInvoicedPricingPeriodIndexes(
  invoices: { positions?: { lane?: string }[] }[],
): Set<number> {
  const indexes = new Set<number>();
  for (const invoice of invoices) {
    for (const position of invoice.positions ?? []) {
      const match = position.lane?.match(RENTAL_FEE_PERIOD_LANE);
      if (match) {
        indexes.add(Number(match[1]));
      }
    }
  }
  return indexes;
}

export function isSameRentalLength(
  oldStart: Date,
  oldEnd: Date,
  newStart: Date,
  newEnd: Date,
): boolean {
  return calculateRentalDays(oldStart, oldEnd) === calculateRentalDays(newStart, newEnd);
}

export function invoiceNumbersForPeriod(
  invoices: { completeInvoiceNumber?: string | null; positions?: { lane?: string }[] }[],
  periodIndex: number,
): string[] {
  const lane = `rental_fee:${periodIndex}`;
  return invoices
    .filter((invoice) => invoice.positions?.some((position) => position.lane === lane))
    .map((invoice) => invoice.completeInvoiceNumber)
    .filter((number): number is string => !!number);
}

type LongTermPricingWithSnapshot = LongTermPricing & {
  extrasSnapshot?: ShortTermPricing['availableExtras'];
};

function extrasCatalog(pricing: Pricing): NonNullable<ShortTermPricing['availableExtras']> {
  if (isLongTermPricing(pricing)) {
    const snapshot = (pricing as LongTermPricingWithSnapshot).extrasSnapshot;
    return (snapshot?.length ? snapshot : pricing.availableExtras) ?? [];
  }
  return pricing.availableExtras ?? [];
}

export function getPricingExtrasForPeriod(
  pricing: Pricing,
  periodIndex?: number | null,
  timezone = DEFAULT_BOOKING_TIMEZONE,
): {
  bookedExtras: BookedExtra[];
  customBookedExtras: CustomBookedExtraDto[];
  catalog: NonNullable<ShortTermPricing['availableExtras']>;
  days: number;
} {
  if (isShortTermPricing(pricing)) {
    return {
      bookedExtras: pricing.bookedExtras ?? [],
      customBookedExtras: pricing.customBookedExtras ?? [],
      catalog: pricing.availableExtras ?? [],
      days: pricing.days || 1,
    };
  }
  const period =
    periodIndex != null
      ? (pricing.periods[periodIndex] ?? null)
      : getCurrentPricingPeriod(pricing, new Date(), timezone);
  const days = period ? getPeriodDays(period) : 1;
  return {
    bookedExtras: period?.bookedExtras ?? [],
    customBookedExtras: period?.customBookedExtras ?? [],
    catalog: extrasCatalog(pricing).map((extra) => ({
      ...extra,
      options: (extra.options ?? []).map((option) => ({
        ...option,
        calculatedPrice: periodExtraOptionPrice(
          option,
          days,
          pricing.taxPercent ?? REGULAR_TAX_PERCENT,
          isLongTermPricing(pricing),
        ),
      })),
    })),
    days,
  };
}
